import type { ReactNode } from "react";
import { HEAT_INK_STRONG_STEP, HEAT_RAMP, heatFill, heatStepOf } from "./chartTheme";
import type { CountFormatter, CountHeatmapProps, HeatmapDatum } from "./contract";
import styles from "./CountHeatmap.module.css";

/**
 * 개수 히트맵. CSS grid DOM 으로 구현한다.
 *
 * 좌표 계산만 필요하므로 라이브러리 없이 그린다(QuantileRangeStrip 과 같은 이유). 색은 보조
 * 채널이다. 셀마다 개수를 직접 적으므로 색각 이상이나 인쇄에서도 값이 남는다. 표 시맨틱(role)을
 * 써서 스크린리더가 행·열 머리와 셀을 연결할 수 있게 한다. 0 셀은 칠하지 않는다. "없음" 은 램프의
 * 가장 옅은 단계가 아니라 빈 배경이어야 한다.
 */
export function CountHeatmap({
  rows,
  columns,
  data,
  formatCount,
  caption,
}: CountHeatmapProps): ReactNode {
  const cellByKey = new Map(data.map((datum) => [cellKey(datum.rowKey, datum.columnKey), datum]));
  return (
    <div className={styles.scroll}>
      <div
        role="table"
        aria-label={caption}
        className={styles.table}
        // 트랙은 표가 소유한다. 행마다 grid 를 나누면 라벨 폭이 달라 열이 어긋난다.
        style={{ gridTemplateColumns: `max-content repeat(${columns.length}, minmax(56px, 1fr))` }}
      >
        <div role="row" className={styles.row}>
          <span role="columnheader" className={styles.corner} aria-label="작업(task)" />
          {columns.map((column) => (
            <span
              key={column.key}
              role="columnheader"
              className={column.unknown === true ? styles.unknownHeader : styles.columnHeader}
              title={column.label}
            >
              {column.label}
            </span>
          ))}
        </div>
        {rows.map((row) => (
          <div key={row.key} role="row" className={styles.row}>
            <span
              role="rowheader"
              className={row.unknown === true ? styles.unknownRowHeader : styles.rowHeader}
              title={row.label}
            >
              {row.label}
            </span>
            {columns.map((column) => (
              <HeatCell
                key={column.key}
                datum={cellByKey.get(cellKey(row.key, column.key))}
                formatCount={formatCount}
              />
            ))}
          </div>
        ))}
      </div>
      {/* 값은 셀에 직접 적혀 있다. 범례는 방향만 알려 주는 장식이라 보조기기에서 뺀다. */}
      <div className={styles.legend} aria-hidden="true">
        <span className={styles.legendLabel}>적음</span>
        {HEAT_RAMP.map((fill) => (
          <span key={fill} className={styles.swatch} style={{ background: fill }} />
        ))}
        <span className={styles.legendLabel}>많음</span>
      </div>
    </div>
  );
}

function cellKey(rowKey: string, columnKey: string): string {
  // 라벨 값에 나올 수 없는 구분자. 공백이 있는 키끼리 충돌하지 않게 한다.
  return `${rowKey}\u0000${columnKey}`;
}

function HeatCell({
  datum,
  formatCount,
}: {
  datum: HeatmapDatum | undefined;
  formatCount: CountFormatter;
}): ReactNode {
  if (datum === undefined || datum.count === 0) {
    return (
      <span role="cell" className={`${styles.cell} ${styles.zero}`}>
        {formatCount(0)}
      </span>
    );
  }
  const strongInk = heatStepOf(datum.intensity) >= HEAT_INK_STRONG_STEP;
  return (
    <span
      role="cell"
      className={strongInk ? `${styles.cell} ${styles.strongInk}` : styles.cell}
      style={{ background: heatFill(datum.intensity) }}
    >
      {formatCount(datum.count)}
    </span>
  );
}
